import { Component, HostListener, OnInit, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, RouterModule, NavigationEnd } from "@angular/router";
import { ButtonModule } from "primeng/button";
import { MenubarModule } from "primeng/menubar";
import { MenuItem } from "primeng/api";
import { SessionService } from "../../services/session.service";
import { TooltipModule } from "primeng/tooltip";
import { MenuModule } from "primeng/menu";
import { AvatarModule } from "primeng/avatar";
import { VisitStoreButtonComponent } from "../visit-store-button/visit-store-button.component";
import { QPilotService, Site } from "../../services/qpilot.service";
import { Subject, filter, takeUntil } from "rxjs";

@Component({
  selector: "app-header",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    MenubarModule,
    TooltipModule,
    MenuModule,
    AvatarModule,
    VisitStoreButtonComponent,
  ],
  templateUrl: "./header.component.html",
  styleUrl: "./header.component.scss",
})
export class HeaderComponent implements OnInit, OnDestroy {
  items: MenuItem[] = [];
  userMenuItems: MenuItem[] = [];
  mobileMenuItems: MenuItem[] = [];
  isScrolled = false;

  // Visit Store Button state
  shouldShowVisitStoreButton = false;
  siteUrl: string | null = null;
  currentSiteId: number | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    public readonly sessionService: SessionService,
    private readonly qpilotService: QPilotService,
  ) {
    this.initializeMenu();
    this.initializeUserMenu();
    this.initializeMobileMenu();
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        this.checkRoute(event.urlAfterRedirects);
      });

    // Check current route on init
    this.checkRoute(this.router.url);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener("window:scroll")
  onWindowScroll() {
    this.isScrolled = window.scrollY > 10;
  }

  /**
   * Initializes the main navigation items
   */
  private initializeMenu(): void {
    this.items = [
      {
        label: "Sites",
        icon: "pi pi-globe",
        routerLink: ["/sites"],
      },
    ];

    // Add admin-only buttons if user is an Administrator
    if (this.isUserAdmin()) {
      this.items.push(
        {
          label: "Admin Dashboard",
          icon: "pi pi-shield",
          routerLink: [
            "/widgets/admin-dashboard/administration-dashboard-site-summary",
          ],
        },
        {
          label: "Administration Edit Entity",
          icon: "pi pi-plus-circle",
          routerLink: ["/widgets/admin-dashboard/administration-edit-entity"],
        },
      );
    }

    // Add help button for all users
    this.items.push({
      label: "Contact Support",
      icon: "pi pi-question-circle",
      url: "mailto:customers@qpilot.cloud",
    });
  }

  /**
   * Initializes the user menu items
   */
  private initializeUserMenu(): void {
    this.userMenuItems = [
      {
        label: "Profile",
        icon: "pi pi-user-edit",
        routerLink: ["/my-account/profile"],
      },
      {
        label: "Subscription",
        icon: "pi pi-credit-card",
        routerLink: ["/my-account/subscription"],
      },
      {
        label: "Apps",
        icon: "pi pi-th-large",
        routerLink: ["/my-account/apps"],
      },
      {
        separator: true,
      },
      {
        label: "Logout",
        icon: "pi pi-sign-out",
        command: () => this.logout(),
      },
    ];
  }

  /**
   * Logs the user out
   */
  logout(): void {
    this.sessionService.clearSession();
    void this.router.navigate(["login"]);
  }

  /**
   * Initializes the mobile menu items with groups
   */
  private initializeMobileMenu(): void {
    this.mobileMenuItems = [
      {
        label: "Navigation",
        items: [
          {
            label: "Sites",
            icon: "pi pi-globe",
            routerLink: ["/sites"],
          },
        ],
      },
    ];

    // Add admin group if user is an Administrator
    if (this.isUserAdmin()) {
      this.mobileMenuItems.push({
        label: "Administration",
        items: [
          {
            label: "Admin Dashboard",
            icon: "pi pi-shield",
            routerLink: ["/widgets/admin-dashboard"],
          },
          {
            label: "Administration Edit Entity",
            icon: "pi pi-plus-circle",
            routerLink: ["/widgets/administration-edit-entity"],
          },
        ],
      });
    }

    // Add support and user actions
    this.mobileMenuItems.push(
      {
        label: "Support",
        items: [
          {
            label: "Contact Support",
            icon: "pi pi-question-circle",
            url: "mailto:customers@qpilot.cloud",
          },
        ],
      },
      {
        label: "Account",
        items: [
          {
            label: "Profile",
            icon: "pi pi-user-edit",
            routerLink: ["/my-account/profile"],
          },
          {
            label: "Subscription",
            icon: "pi pi-credit-card",
            routerLink: ["/my-account/subscription"],
          },
          {
            label: "Apps",
            icon: "pi pi-th-large",
            routerLink: ["/my-account/apps"],
          },
          {
            separator: true,
          },
          {
            label: "Logout",
            icon: "pi pi-sign-out",
            command: () => this.logout(),
          },
        ],
      },
    );
  }

  /**
   * Checks if the current user is an Administrator
   */
  private isUserAdmin(): boolean {
    const role = this.sessionService.user?.Role;
    return typeof role === "string" && role.indexOf("Administrator") > -1;
  }

  /**
   * Checks the current route and updates Visit Store button state
   */
  private checkRoute(url: string): void {
    // Show button only on /widgets routes
    this.shouldShowVisitStoreButton = url.includes("/widgets");

    if (this.shouldShowVisitStoreButton) {
      // Extract siteId from URL (e.g., /widgets/dashboard/123)
      const siteIdMatch = url.match(/\/widgets\/[^/]+\/(\d+)/);
      if (siteIdMatch && siteIdMatch[1]) {
        const siteId = parseInt(siteIdMatch[1], 10);
        if (siteId !== this.currentSiteId) {
          this.currentSiteId = siteId;
          this.loadSiteData(siteId);
        }
      } else {
        this.siteUrl = null;
        this.currentSiteId = null;
      }
    } else {
      this.siteUrl = null;
      this.currentSiteId = null;
    }
  }

  /**
   * Loads site data for the Visit Store button
   */
  private loadSiteData(siteId: number): void {
    const accessToken = this.sessionService.accessToken;
    if (!accessToken) {
      return;
    }

    this.qpilotService
      .getSite(siteId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (site: Site) => {
          this.siteUrl = site.Url || null;
        },
        error: (error: unknown) => {
          console.error("Error loading site data:", error);
          this.siteUrl = null;
        },
      });
  }
}
